<script setup>
import {nextTick, provide, ref, watch} from "vue";
import projectsTasksService from "@/services/projects-tasks.service";
import constants from "@/constants/constants";
import ProjectTaskActivities from "@/components/tasks/viewProjectTask/ProjectTaskActivities.vue";
import StartupsSelector from "@/components/ui/StartupsSelector.vue";
import startupsService from "@/services/startups.service";
import DatePicker from "vue2-datepicker";
import {decodeDate, decodeDateFromServer, encodeDateForServer} from "@/helpers/date";
import ProjectTaskFiles from "@/components/tasks/viewProjectTask/ProjectTaskFiles.vue";
import ProjectTaskTeam from "@/components/tasks/viewProjectTask/ProjectTaskTeam.vue";

const emit = defineEmits(['taskUpdated', 'commentAdded']);

const showModal = ref(false);
const taskId = ref(null);
const task = ref(null);
const startup = ref(null);
const date = ref(null);
const editField = ref(null);
const activitiesBlock = ref(null);
const newFieldValue = ref('');

const newStartup = ref(null);
const newDate = ref(null);

watch(editField, (value, oldValue) => {
    if (value) {
        if (value === 'startup_id') {
            newStartup.value = {
                ...startup.value
            };
        } else if (value === 'due_date') {
            newDate.value = date.value;
        } else {
            newFieldValue.value = task.value[value];
        }
    } else {
        let fieldName = oldValue;

        if (fieldName === 'startup_id') {
            if (newStartup.value?.id !== startup.value?.id) {
                updateTaskField(fieldName, newStartup.value?.id || null);
            }
        } else if (fieldName === 'due_date') {
            let newDateForServer = newDate.value ? encodeDateForServer(newDate.value) : null;

            if (newDateForServer !== task.value[fieldName]) {
                updateTaskField(fieldName, newDateForServer);
            }
        } else {
            if (newFieldValue.value !== task.value[fieldName]) {
                updateTaskField(fieldName, newFieldValue.value);
            }
        }
    }
});

watch(() => task.value?.startup_id, value => {
    if (value) {
        getStartupsData();
    } else {
        startup.value = null;
    }
});

watch(() => task.value?.due_date, value => {
    if (value) {
        date.value = decodeDateFromServer(value);
    } else {
        date.value = null;
    }
});

async function getData() {
    const {data} = await projectsTasksService.getTaskData(taskId.value);
    task.value = data;
}

async function updateTaskField(field, value) {
    const {data} = await projectsTasksService.updateTask(taskId.value, {[field]: value});

    task.value[field] = data;
    emit('taskUpdated');
    updateActivities();

    if (field === 'user_id') {
        getWatchers();
    }
}

async function getStartupsData() {
    const {data} = await startupsService.getStartupDataForSelect(task.value.startup_id);
    startup.value = data;
}

function updateActivities() {
    activitiesBlock.value.getActivities();
}

function addWatcher(userId) {
    projectsTasksService.addWatcher(taskId.value, userId);
}

function removeWatcher(userId) {
    projectsTasksService.removeWatcher(taskId.value, userId);
}

async function getWatchers() {
    const {data} = await projectsTasksService.getWatchers(taskId.value);

    task.value.watchers_ids = data;
}

async function setEditField(field) {
    if (editField.value === field) return;
    if (editField.value !== null) {
        editField.value = null;
        await nextTick();
    }
    editField.value = field;
}

function closeEdit() {
    editField.value = null;
}

async function deleteTask() {
    if (!confirm('Delete task?')) {
        return;
    }

    await projectsTasksService.deleteTasks({ids: [taskId.value]});

    emit('taskUpdated');
    showModal.value = false;
}

function open(id) {
    taskId.value = id;
    task.value = null;

    getData();
    showModal.value = true;
}

provide('editField', editField);

defineExpose({
    open,
});
</script>

<template>
    <b-modal v-model="showModal" modal-class="modal-w-xxl modal-access" hide-footer>
        <template v-if="task" #modal-header>
            <div style="width: 100%; display: flex; justify-content: space-between; align-items: center;">
                <input
                    v-if="editField === 'title'"
                    v-click-outside="closeEdit"
                    v-model="newFieldValue"
                    v-focus
                    @keydown.enter="closeEdit"
                />

                <div v-else class="editable" @click.stop="setEditField('title')">
                    <h5 class="modal-title">{{task.title}}</h5>
                </div>

                <button @click="showModal = false" class="close"></button>
            </div>
        </template>

        <div v-if="task" class="access-content">
            <div class="access-content__main">
                <div class="d-flex mb-4">
                    <b-dropdown :text="constants.tasksStatus[task.status]" class="status-badge mr-3" :class="`status-badge--${task.status}`">
                        <b-dropdown-item
                            v-for="(label, value) in constants.tasksStatus"
                            :key="value"
                            v-show="value !== task.status"
                            class="status-badge"
                            :class="`status-badge--${value}`"
                            @click="updateTaskField('status', value)"
                        >
                            {{ label }}
                        </b-dropdown-item>
                    </b-dropdown>

                    <b-dropdown :text="constants.tasksPriorities[task.priority] + ' Priority'" class="status-badge priority-note" :class="`priority-note--${task.priority}`">
                        <b-dropdown-item
                            v-for="(label, value) in constants.tasksPriorities"
                            :key="value"
                            v-show="value !== task.priority"
                            class="priority-note"
                            :class="`status-badge__${value} priority-note--${value}`"
                            @click="updateTaskField('priority', value)"
                        >
                            {{ label }} Priority
                        </b-dropdown-item>
                    </b-dropdown>
                </div>

                <div class="form__item mb-4" v-if="editField === 'description'">
                    <textarea
                        v-click-outside="closeEdit"
                        v-model="newFieldValue"
                        v-focus
                        class="form-control form-control--mid"
                    />
                </div>

                <div v-else class="editable" @click.stop="setEditField('description')">
                    <p v-if="task.description?.length" v-html="task.description?.replace(/\r?\n/g, '<br>')" />
                    <p v-else class="text-muted">Add a description</p>
                </div>

                <dl class="definition-list definition-list--secondary">
                    <dt>Company</dt>
                    <dd>
                        <StartupsSelector
                            v-if="editField === 'startup_id'"
                            v-click-outside="closeEdit"
                            v-model="newStartup"
                            @changed="closeEdit"
                        />

                        <div v-else class="editable" @click.stop="setEditField('startup_id')">
                            {{ startup ? startup.name : 'No company' }}
                        </div>
                    </dd>

                    <dt>Reporter</dt>
                    <dd>
                        <span :class="{deleted: task.reporter.deleted}">
                            {{task.reporter.name}}
                        </span>
                    </dd>

                    <dt>Due Date</dt>
                    <dd>
                        <DatePicker
                            v-if="editField === 'due_date'"
                            v-click-outside="closeEdit"
                            v-model="newDate"
                            placeholder="Select a date"
                            format = "MM/DD/YYYY"
                            class="mb-2 w-100"
                            :append-to-body="false"
                        />

                        <div v-else class="editable" @click.stop="setEditField('due_date')">
                            {{ date ? decodeDate(date) : 'No date' }}
                        </div>
                    </dd>
                </dl>

                <h3 class="heading3">Activity</h3>

                <ProjectTaskActivities
                    ref="activitiesBlock"
                    :task-id="task.id"
                    @commentAdded="emit('commentAdded')"
                />
            </div>

            <aside class="access-content__side">
                <ProjectTaskTeam
                    :user-id="task.user_id"
                    :project-id="task.project_id"
                    :init-watchers="task.watchers_ids"
                    @updateUserId="(id) => updateTaskField('user_id', id)"
                    @addWatcher="addWatcher"
                    @removeWatcher="removeWatcher"
                />

                <ProjectTaskFiles
                    :task-id="task.id"
                    @fileUploaded="updateActivities"
                />

                <b-dropdown id="" text="Manage Task" class="dropdown-airy dropdown-airy--manage">
                    <b-dropdown-item @click="deleteTask">
                        Delete Task
                    </b-dropdown-item>
                </b-dropdown>
            </aside>
        </div>
    </b-modal>
</template>

<style scoped>
    .editable {
        padding: 2px;
        
        &:hover {
            background-color: rgba(0, 0, 0, 0.1);
            cursor: pointer;
        }
    }
</style>
